/**
 * Every word the inbox prints, keyed by notification type.
 *
 * The tables here are the product's opinion of each type — which tab it wants,
 * what it is called, what the button says — and they are tables rather than
 * switches so the model can stay a lookup and the tests can stay on the model.
 * Two of the placements are argued inline because they look wrong at a glance:
 * an offer of title is an ask, and a consignment offer is not, since the inbox
 * has no control that could answer the latter.
 */

import type { NotificationType } from "@ondo/shared";

import type { TagTone } from "../lib/Tag.tsx";

/**
 * The tabs, and what each one is for.
 *
 * Four groups over seven types, because the reader's question is what a row
 * WANTS from them rather than which table it came from: something is being asked
 * of you, something you asked has come back, somebody has claimed a connection,
 * or a record you hold has changed. `all` is first because most inboxes are
 * short enough not to need the others.
 */
export const NOTIFICATION_TABS = [
  { id: "all", label: "All" },
  { id: "asks", label: "Asked of you" },
  { id: "answers", label: "Answers" },
  { id: "affiliations", label: "Affiliations" },
  { id: "record", label: "Record" },
] as const;

export type NotificationTab = (typeof NOTIFICATION_TABS)[number]["id"];

/** Which tab each type falls under. `all` is not in here — it is every row. */
export const TAB_OF: Record<NotificationType, Exclude<NotificationTab, "all">> = {
  "verification.requested": "asks",
  "verification.referred": "asks",
  "verification.answered": "answers",
  // Not "answers". Nothing was asked, so nothing has come back — this is the
  // reader's own record having changed, which is what the tab is for.
  "verification.attested": "record",
  // "asks" ON THE FIRST AND "answers" ON THE SECOND, which is the one place
  // OND-60's three steps part company on this screen. A studio asking for two
  // more photographs is a question waiting on the reader and nothing else will
  // move until they send them. A revised date is an answer to a question the
  // reader already asked — "when will I hear" — and there is nothing to do
  // about it.
  //
  // NEITHER IS IN `isAsk`, and the amber chip is withheld for the same reason
  // `transfer.offered` once was: `isAsk` makes a row render as AWAITING, and the
  // API can only answer that against a fact Postgres holds. Whether a studio
  // still wants the photographs is the studio's judgement and not a column, so
  // the tab counts the row and the chip does not promise it is open.
  "verification.evidence_requested": "asks",
  "verification.extended": "answers",
  // "asks", because it is: somebody at the studio now holds a case and is
  // expected to look at it. Not "record" — this is not news about the reader's
  // own holdings, it is work.
  "verification.assigned": "asks",
  // "record", and NOT "asks", even though the claim it reports plainly wants an
  // answer. The reader is not the party who may give it — whoever was asked is
  // somebody else, and this verb does not fire for the party asked — so an
  // amber ASK chip here would be a promise the build cannot keep.
  "attribution.claimed": "record",
  "affiliation.claimed": "affiliations",
  "affiliation.answered": "affiliations",
  "work.registered": "record",
  "collection.shared": "record",
  // "asks" on the offer now, and the two beside it stay "record". This used to
  // say the opposite, on the grounds that the API could only answer `awaiting`
  // through a target kind it lacked — so an offer of title landed as news. It
  // reads `ondo.my_transfer_offers()` for that answer now, and a question that
  // the reader can settle by pressing Accept belongs on the tab that counts
  // questions.
  "transfer.offered": "asks",
  "transfer.answered": "record",
  "transfer.withdrawn": "record",
  // "record", and it is the reader's own act that changed rather than
  // somebody else's. Not "answers": nobody asked anything. Not "asks": there
  // is nothing on this screen or any other to answer, because what happened
  // is that ONDO gave up recording a sale and took it back.
  "sale.reversed": "record",
  // "record" and not "answers", matching `CHIPS` and `isAsk`: nobody asked the
  // seller anything. The buyer took a position on the work's own record, and
  // there is no door on this screen or any other that answers a dispute — so
  // filing it under "Asked of you" would send a reader to a tab whose whole
  // promise is that the thing can be answered there. It moves when a screen
  // exists that answers it, with the definer that says whether it is still
  // open — the order `settlement.remitted` learned the hard way.
  "claim.disputed": "record",
  // "asks" for the proposal, because this one genuinely is one: the record does
  // not change until the reader answers, and unlike a consignment offer there
  // is no inventory screen of their own that lists it. The two answers are
  // "answers", which is what that tab means.
  "sale.addition_proposed": "asks",
  "sale.addition_answered": "answers",
  "sale.addition_approved": "answers",
  // Nothing is being asked, so not "asks". Being named as an agent is the
  // reader's own standing having changed, which is what this tab is for.
  "agent.assigned": "record",
  "agent.removed": "record",
  // "record" and not "asks", even though there IS an act available — either side
  // may renew alone. It is not an ASK because nobody is waiting on the reader:
  // letting a term lapse is a perfectly good answer, and a row that sat
  // outstanding on an inbox until a date the reader was content to let pass
  // would be the tab lying about what is owed.
  "mandate.expiring": "record",
  "mandate.expired": "record",
  // "record" and not "asks", which is the tab these three look like they want.
  // A client told their agent changed firms plainly has something to think
  // about — but `TAB_OF` has to agree with `isAsk` in the API, and nothing is
  // waiting on an answer: reviewing a scope you already recorded is a decision
  // you may reasonably make by deciding to leave it alone. The amber ASK chip on
  // a row that no tile counts is the promise `consignment.offered` records this
  // table refusing to make — `loan.proposed` was the other example until OND-9
  // built the screen that could keep it, and it is now "asks" below.
  "agent.organisation_joined": "record",
  "agent.organisation_left": "record",
  "agent.authority_changed": "record",
  // "asks" for the request and "answers" for the reply, which is the same split
  // the verification pair gets and for the same reason: one is waiting on the
  // reader and the other is a question the reader raised coming back.
  "representation.requested": "asks",
  "representation.answered": "answers",
  // "record", not "asks", and this is the one placement worth arguing.
  //
  // An offer plainly wants an answer — but a gallery answers it on its INVENTORY
  // screen, which reads the live rows from Postgres, and this inbox has no
  // control that could. Filing it under "Asked of you" would send a reader to a
  // tab whose whole promise is that the thing can be answered there.
  // `consignment.offered` is deliberately absent from `ASKS` for the same
  // reason — see the variant in the API's `notification.events.ts`.
  "consignment.offered": "record",
  "consignment.answered": "answers",
  "consignment.ended": "record",
  // "record" on the proposal, for `consignment.offered`'s reason: it is not in
  // `ASKS`, so an asks tab counting it would promise an answer this screen
  // cannot take. New terms are answered on the work's own record.
  "consignment.revision_proposed": "record",
  "consignment.revision_answered": "answers",
  // "record" ON THE OFFER, for `consignment.revision_proposed`'s reason: it is
  // not in `ASKS`, so an asks tab counting it would promise an answer this
  // screen cannot take — the owner answers an offer on the work's own record,
  // where the figure is.
  "offer.received": "record",
  // "record" AND NOT "answers", which is the one of the three worth arguing.
  // The answers tab is for something the reader ASKED coming back, and nobody
  // asked: the floor refused this offer with nobody acting. For the buyer it is
  // the nearest thing to an answer they will get, and it is still news about a
  // record rather than a reply to a question.
  "offer.refused": "record",
  // And this one IS an answer, for the buyer and the gallery alike: the owner
  // was asked and said something.
  "offer.answered": "answers",
  // "asks" on the proposal, and this is the move the previous comment promised.
  //
  // It said "record", for the consignment offer's reason: "accept and decline
  // are OND-9's routes, so there is no control on this screen or any other that
  // could give one… It moves when the screen exists, with the definer that says
  // whether it is still open — the order `settlement.remitted` learned the hard
  // way." OND-9 built all three in one commit: the doors, the Borrowed screen
  // that presses them, and `ondo.borrower_loans()` as the read that says whether
  // a proposal still stands. `loan.proposed` is in `ASKS` and the read service
  // resolves `awaiting` from that definer, so the condition is met and the
  // promise is kept — a reader sent to "Asked of you" finds a row whose "View
  // record" lands on the screen with Accept and Decline on it.
  //
  // WITHOUT THIS THE TWO HALVES DISAGREED, visibly: `toRow` computes `settled`
  // from `isAsk`, so an answered proposal already swapped its chip for
  // ANSWERED — a row that wore an answer's chip while filed as news and counted
  // by no tile.
  "loan.proposed": "asks",
  "loan.answered": "answers",
  "loan.ended": "record",
  // `settlement.remitted` is the only one of the three in `ASKS`: a gallery has
  // recorded paying you and you may say otherwise. The other two are news about
  // money — one arriving, one being answered.
  "settlement.due": "record",
  "settlement.remitted": "asks",
  "settlement.answered": "answers",
};

/**
 * The chip, in three tones.
 *
 * `ask` is amber and not red. A verification request is not a fault and not
 * urgent — nobody is obliged to answer one, which the Verifications screen says
 * outright — so dressing it as an error would make an ordinary courtesy read as
 * a problem. See `--warn-*` in index.css.
 */
export const CHIPS: Record<NotificationType, { label: string; tone: Tone }> = {
  "verification.requested": { label: "ASK", tone: "ask" },
  "verification.referred": { label: "PASSED TO YOU", tone: "ask" },
  "verification.answered": { label: "ANSWER", tone: "answer" },
  "verification.attested": { label: "NEWS", tone: "news" },
  // NEWS, GREY, ON ALL THREE, and on the first two that is deliberately not
  // what the tab says. `TAB_OF` files the evidence request under "Asked of
  // you"; the chip stays grey because amber is reserved for rows the API can
  // say are still open, and nothing in this schema records whether the studio
  // has what it wanted. A row that said ASK for a year after the collector sent
  // the photographs would be the promise `isAsk`'s own comment warns about.
  //
  // "MORE WANTED" rather than "EVIDENCE": the label sits beside a work's title
  // in a list and has to read as a sentence fragment, and "evidence" is a word
  // for a tribunal.
  "verification.evidence_requested": { label: "MORE WANTED", tone: "news" },
  "verification.extended": { label: "NEW DATE", tone: "news" },
  "verification.assigned": { label: "ASSIGNED", tone: "news" },
  "attribution.claimed": { label: "ATTRIBUTION", tone: "news" },
  "affiliation.claimed": { label: "ASK", tone: "ask" },
  "affiliation.answered": { label: "ANSWER", tone: "answer" },
  "work.registered": { label: "NEWS", tone: "news" },
  "collection.shared": { label: "NEWS", tone: "news" },
  "agent.assigned": { label: "NEWS", tone: "news" },
  "agent.removed": { label: "NEWS", tone: "news" },
  "mandate.expiring": { label: "NEWS", tone: "news" },
  "mandate.expired": { label: "NEWS", tone: "news" },
  // NEWS, grey, matching `TAB_OF` and `isAsk`. The temptation is the amber ASK,
  // because a client reading that their agent has left a firm is being invited
  // to do something — and the something is to look, not to answer. Amber is for
  // what somebody is waiting on; nobody is waiting on this.
  "agent.organisation_joined": { label: "NEWS", tone: "news" },
  "agent.organisation_left": { label: "NEWS", tone: "news" },
  "agent.authority_changed": { label: "NEWS", tone: "news" },
  "representation.requested": { label: "ASK", tone: "ask" },
  "representation.answered": { label: "ANSWER", tone: "answer" },
  "consignment.offered": { label: "NEWS", tone: "news" },
  "consignment.answered": { label: "ANSWER", tone: "answer" },
  "consignment.ended": { label: "NEWS", tone: "news" },
  // NEWS on the proposal rather than the amber ASK it looks like it wants, for
  // the reason the loan proposal below is NEWS: the chip has to agree with
  // `TAB_OF` and with `isAsk` in the API, and new terms are answered on the
  // work's own record rather than from the inbox.
  "consignment.revision_proposed": { label: "NEWS", tone: "news" },
  "consignment.revision_answered": { label: "ANSWER", tone: "answer" },
  // NEWS on the offer rather than the amber ASK it looks like it wants, for the
  // reason the revision proposal above is NEWS: the chip has to agree with
  // `TAB_OF` and with `isAsk` in the API, and an offer is answered on the work's
  // own record rather than from the inbox.
  "offer.received": { label: "NEWS", tone: "news" },
  // NEWS and not ANSWER, matching `TAB_OF`: nobody answered. The floor refused
  // it, which is news to all three parties at once.
  "offer.refused": { label: "NEWS", tone: "news" },
  "offer.answered": { label: "ANSWER", tone: "answer" },
  // ASK on the proposal, for `transfer.offered`'s reason directly below and in
  // the same words: the API learned to say when it stopped being one.
  //
  // THIS SAID NEWS UNTIL OND-9, on the grounds that "a row wearing an amber ASK
  // that no tab counts and no screen can answer is a promise the build does not
  // keep" — which was exactly right while it was true. It stopped being true in
  // `20261003094000_borrower_loan_lifecycle.sql`: `loan.proposed` is in `ASKS`
  // in `packages/shared/src/domain/notifications.ts`, the read service answers
  // `awaiting` for it through `ondo.borrower_loans()`, and the museum's
  // Borrowed screen is the screen that can answer it — which is where this
  // row's own "View record" already goes. The chip was the half left behind, so
  // a question the API reported as waiting on the reader wore grey NEWS.
  "loan.proposed": { label: "ASK", tone: "ask" },
  "loan.answered": { label: "ANSWER", tone: "answer" },
  "loan.ended": { label: "NEWS", tone: "news" },
  // ASK, since the API learned to say when it stopped being one. The amber
  // chip goes out the moment the offer is answered or withdrawn.
  "transfer.offered": { label: "ASK", tone: "ask" },
  "transfer.answered": { label: "ANSWER", tone: "answer" },
  "transfer.withdrawn": { label: "NEWS", tone: "news" },
  // "ask" amber rather than "news" grey, and it is the only news row in this
  // table that wears it. The chip's comment says amber is for something that
  // "is not a fault and not urgent"; this is a fault and it is ONDO's. Nobody
  // is obliged to do anything, but a sale they believe they recorded is gone,
  // and a grey NEWS chip beside that sentence would read as reassurance.
  "sale.reversed": { label: "REVERSED", tone: "ask" },
  // DISPUTED, and amber, which is the one chip on this list that is neither an
  // ask nor ordinary news. Nothing is owed and nothing is waiting — but a buyer
  // denying a sale is the most serious thing that can be said about a work's
  // record, and a grey NEWS chip beside it would read as housekeeping.
  // `sale.reversed` wears an `ask` tone without being an ask for the same
  // reason and set the precedent.
  "claim.disputed": { label: "DISPUTED", tone: "ask" },
  "sale.addition_proposed": { label: "ASK", tone: "ask" },
  "sale.addition_answered": { label: "ANSWER", tone: "answer" },
  "sale.addition_approved": { label: "ANSWER", tone: "answer" },
  "settlement.due": { label: "DUE", tone: "news" },
  "settlement.remitted": { label: "ASK", tone: "ask" },
  "settlement.answered": { label: "ANSWER", tone: "answer" },
};

export type Tone = "ask" | "answer" | "news" | "done";

/**
 * What an ask looks like once it has been answered.
 *
 * The row must change, and this is the whole reason: `awaiting` is read from the
 * record on every load, so answering something in the Verifications screen
 * settles it here immediately — but a row that still wore an amber ASK chip and
 * a "Review" button would be reporting the opposite of what it knows. Somebody
 * who had just answered would reasonably conclude the two screens disagreed.
 *
 * Note what does NOT change: the row stays in the tab it was in, and it does not
 * become read. It is still a thing that was asked of you, and reading and
 * answering are different acts — see `NotificationEntry.awaiting`.
 */
export const RESOLVED = {
  chip: { label: "ANSWERED", tone: "done" as Tone },
  action: "View",
  detail: {
    verification: "You have answered this. What was recorded stands beside the link.",
    affiliation: "You have answered this.",
  },
} as const;

export const TITLES: Record<NotificationType, string> = {
  "verification.requested": "Verification requested",
  "verification.referred": "A verification request was passed to you",
  "verification.answered": "Your verification request was answered",
  "verification.attested": "Somebody took a position on your entry",
  // "the studio" AND NOT ITS NAME, matching every headline in this map: the row
  // prints the counterparty separately, and a name in the sentence would be a
  // second copy of it going stale beside the first.
  "verification.evidence_requested": "The studio has asked for more evidence",
  // "target" AND NEVER "deadline". It is a target and not a guarantee of
  // authentication, which is the sentence OND-60 asks to be shown clearly — and
  // nothing happens when it passes.
  "verification.extended": "The studio has revised its target date",
  "verification.assigned": "A confirmation request was assigned to you",
  "attribution.claimed": "A work has been attributed to your artist",
  "affiliation.claimed": "An affiliation was claimed with you",
  "affiliation.answered": "Your affiliation claim was answered",
  "work.registered": "A work was registered into your collection",
  "collection.shared": "A collection was shared with you",
  "agent.assigned": "You were named as the agent for a work",
  "agent.removed": "You are no longer the agent for a work",
  // ONE SENTENCE FOR ALL THREE WARNINGS, and the row does not say which of the
  // three it is. The reader is sent to the record, where the days-remaining line
  // is computed from the term itself; a headline that said "in a month" would be
  // a second copy of a date that can be renewed out from under it, and the three
  // would then stack in the inbox as three contradictory claims.
  "mandate.expiring": "An agent appointment is coming to an end",
  "mandate.expired": "An agent appointment has ended",
  // THE HEADLINE NAMES NO ORGANISATION, on any of the three, and the row prints
  // no counterparty but the agent — whom the API stamps as the actor. Which firm
  // an appointment reaches is on the record under a policy; which firm the agent
  // moved to reaches nothing and is their own business. See the API's
  // `payloadFor`, which carries neither.
  "agent.organisation_joined": "An agent you appointed is now recorded at an organisation",
  // "no longer at" AND NEVER "has left", because one verb covers a departure and
  // a suspension alike — `ondo.principal_ids_at_least()` reads `status =
  // 'active'`, so both reach exactly nothing, and 20261004092000 decision 8 split
  // the verbs on that consequence rather than on the status word. A headline
  // saying "has left" would be false of half the rows that produce it.
  "agent.organisation_left": "An agent you appointed is no longer at an organisation",
  // SAYS SOMEBODY DID IT, because somebody did and it was not the reader: this
  // verb is raised only when a gallery or a delegator changed the reach of an
  // appointment the reader made. An owner changing their own never produces one.
  "agent.authority_changed": "Somebody changed what one of your appointments reaches",
  "representation.requested": "An agent asked to represent one of your works",
  "representation.answered": "Your representation request was answered",
  "consignment.offered": "A work was offered to your gallery",
  "consignment.answered": "Your consignment was answered",
  "consignment.ended": "A consignment ended",
  "consignment.revision_proposed": "New terms were proposed on a consignment",
  "consignment.revision_answered": "Your proposed terms were answered",
  // NO "YOUR" ON ANY OF THE THREE, because each reaches more than one party:
  // the owner and the gallery on the first, all three on the second, the buyer
  // and the gallery on the third. A headline that said "your offer" would be
  // false for whichever recipient did not make it.
  "offer.received": "A price was offered for a work",
  // SAYS THAT NOBODY WAS ASKED, which is the whole of what this row is for. The
  // owner reads it and knows there was interest at the wrong price; the buyer
  // reads it and knows why they heard nothing from anybody.
  "offer.refused": "An offer came in below the agreed floor and was refused",
  "offer.answered": "An offer was answered",
  "loan.proposed": "A loan of a work was proposed to you",
  "loan.answered": "A loan you proposed was answered",
  "loan.ended": "A loan ended",
  "transfer.offered": "A work has been sold to you",
  "transfer.answered": "A sale you effected was answered",
  "transfer.withdrawn": "A sale recorded against you was withdrawn",
  "sale.reversed": "ONDO could not finish recording a sale, and reversed it",
  "claim.disputed": "The buyer recorded off ONDO disputes a sale",
  "sale.addition_proposed": "A sale's record has an addition awaiting your approval",
  "sale.addition_answered": "An addition you proposed to a sale's record was refused",
  "sale.addition_approved": "An addition you proposed is now part of a sale's record",
  "settlement.due": "A resale settlement fell due",
  "settlement.remitted": "A gallery recorded paying you a resale share",
  "settlement.answered": "An artist answered a settlement you recorded",
};

/**
 * The line under the title.
 *
 * Written WITHOUT the counterparty's name, which the row prints separately, and
 * without any of what was actually said. The API deliberately carries no note
 * body and no stance into a feed — the answer lives on the record, and this
 * sends the reader there rather than paraphrasing it somewhere it cannot be
 * checked. See `payloadFor` in the API's `notification.events.ts`.
 */
export const DETAILS: Record<NotificationType, string> = {
  "verification.requested":
    "You have been asked to confirm something you are positioned to know.",
  "verification.referred":
    "Somebody better placed to answer was thought to be you. The question is open with you now.",
  "verification.answered":
    "The answer stands beside the link on the record, with whatever was added to it.",
  "verification.attested":
    "Nobody asked them to. What they recorded stands beside the link on the record.",
  // WHAT IS WANTED IS NOT IN HERE, for this map's stated reason: the API carries
  // no reason into a feed, because the reason lives on the case under the policy
  // that discloses it. "Open the case" is the whole of what this row can
  // honestly say.
  //
  // AND IT SAYS THE REQUEST IS STILL OPEN, which is the thing a reader is most
  // likely to get wrong: being asked for more is not a refusal and not an
  // answer.
  "verification.evidence_requested":
    "Your request is still open. Open the case to see what the studio needs, and nothing is charged for asking.",
  "verification.extended":
    "The case shows what was promised and what is promised now, with the studio's reason. It is a target and not a guarantee.",
  "verification.assigned":
    "It is yours to look at. What you may do with it is unchanged: answering still needs the standing the studio gave you.",
  // IT SAYS THE READER WAS NOT ASKED, and that sentence is the point rather
  // than a caveat. The claim wants an answer and this reader cannot give one,
  // so a row that only said a claim had been made would read as a question
  // going unanswered on their desk.
  "attribution.claimed":
    "Somebody has asked another party to confirm it. You were not asked, and nothing here is waiting on you.",
  "affiliation.claimed":
    "Somebody says they work, or worked, with you. Confirming records the fact and grants nothing.",
  "affiliation.answered": "The claim you made has been answered.",
  "work.registered": "It has an ONDO id and a record of its own.",
  "collection.shared": "You can now see what it holds.",
  "agent.assigned":
    "You can open its record, correct its catalogue entry and add to its history.",
  // Says what it costs rather than only that it happened, because the reader
  // cannot go and look: the work became unreadable the moment the mandate
  // ended, so this row is the last thing that can explain where it went.
  "agent.removed":
    "The record is no longer open to you. Anything you added to its history stands.",
  // Says what will happen and who can stop it, because this is the one
  // notification in the vocabulary that arrives while something can still be
  // done about it. "Either of you" is the literal rule — OND-76's confirmed
  // follow-up — and saying so is what stops each side waiting for the other.
  "mandate.expiring":
    "Either of you can extend it. If nobody does, the agent's access ends on the date agreed.",
  // Says what it cost, for the reason `agent.removed` gives: the work became
  // unreadable to the agent the moment the term ran out, so this row is the last
  // thing that can explain where it went.
  "mandate.expired":
    "The term agreed has run out, so the appointment has ended. Anything added to the work's history stands.",
  // ALL THREE LEAD WITH WHAT DID NOT HAPPEN, which is the opposite of how every
  // other line in this table opens, and it is the whole point of the feature.
  // OND-107: "The notification must not transfer or expand access. The scope
  // recorded at appointment controls access; this tells the client so they can
  // review it." A line that only said the agent had moved would leave the reader
  // to guess whether their picture had moved with them.
  "agent.organisation_joined":
    "Nothing about your appointment has changed, and the new organisation has no access to the work. Open the record to see who your appointment reaches and change it if you want to.",
  "agent.organisation_left":
    "Nothing about your appointment has changed. The agent keeps the access you gave them, and any organisation you named keeps the access you gave it — open the record to review both.",
  "agent.authority_changed":
    "The appointment still names the agent you chose. What it reaches, and whether it may consign, is on the record with who changed it.",
  // Says what granting would DO, because the reader is being asked to decide and
  // "an agent asked" tells them nothing about what they would be handing over.
  // The sentence is the same one the answering screen prints, deliberately: two
  // descriptions of one grant is how they come to disagree.
  "representation.requested":
    "They would be able to read the record, correct the catalogue entry and write the work’s history — and nothing else.",
  "representation.answered":
    "The answer is on the request. If it was granted, the work is in My Representations.",
  // Says where to answer it, because this row cannot be the place: the inbox has
  // no control, and the inventory reads the live row rather than this feed.
  "consignment.offered":
    "It is waiting in your inventory, with its terms. Accepting opens the full record; declining ends the offer and tells them.",
  "consignment.answered":
    "The answer is on the work's own record, under who is holding it.",
  // Says what it costs rather than only that it happened, for the reason
  // `agent.removed` does: ending the arrangement closed the record in the same
  // statement, so this row is the last thing that can explain where it went.
  "consignment.ended":
    "The arrangement is over. If it was yours to read through, the record is no longer open to you.",
  // NO FIGURE IN EITHER SENTENCE, which is the restraint the whole feature
  // keeps: the proposed price is on a row under the policy that decided who may
  // see it, and these rows say that a decision is waiting and where to make it.
  // The first also says what has NOT happened, because that is the thing the
  // reader most needs to be sure of.
  "consignment.revision_proposed":
    "The arrangement carries on unchanged. Nothing moves until every party accepts the same terms — the figures are on the work’s record.",
  "consignment.revision_answered":
    "The answer is on the work’s record, beside the terms it was about. Agreed terms are in force from the moment the last party accepted.",
  // NO FIGURE IN ANY OF THE THREE, the same restraint the revision pair keeps
  // and for the same reason: the offered price is on a row under the policy that
  // decided who may see it, and these rows say what has happened and where to
  // look.
  "offer.received":
    "It is on the work’s record, with the price. Nothing has moved: accepting it sells the work at that figure, and refusing it leaves the consignment exactly as it was.",
  // SAYS WHO DECIDED, WHICH IS NOBODY. This is the sentence the whole ticket
  // turns on: the owner is not being asked, because they already answered this
  // when they agreed the floor — and the buyer is being told that a rule
  // refused them rather than a person.
  "offer.refused":
    "It was below the floor the parties had already agreed, so it was refused as it came in and nobody was asked. It is on the work’s record with the version of the terms it was measured against.",
  "offer.answered":
    "The answer is on the work’s record, beside the offer it was about. An accepted offer is a sale, and title moves under the arrangement it was offered through.",
  // Says what it does NOT do, because that is the thing a borrowing institution
  // most needs to be sure of: a loan moves possession and touches title not at
  // all. No venue and no dates — the API carries neither into a feed, for the
  // reason it carries no price, and the loan itself says both.
  "loan.proposed":
    "A loan moves where a work is and nothing about who owns it. Nothing is agreed until you say so.",
  "loan.answered": "The answer is on the loan, in your own loans.",
  // Says what it costs rather than only that it happened, for the reason
  // `consignment.ended` and `agent.removed` do: ending a loan revokes the
  // borrower's read in the same statement, so this row is the last thing that
  // can explain where the record went.
  "loan.ended":
    "The arrangement is over. If you were reading the record through it, it is no longer open to you.",
  // Says what accepting would DO, because the reader is being asked to decide
  // and "a work has been sold to you" tells them nothing about what changes.
  // The control now exists — the collector's "Offered to you" destination — so
  // this no longer has to apologise for a missing button, only to be clear that
  // reading the record and owning the work are different things.
  "transfer.offered":
    "The record is open to you while the offer stands. Accepting puts the work in a collection of yours and moves title on the registry; it is not yours until you do.",
  "transfer.answered":
    "Whether title moved is on the sale in Sold Works, beside the buyer.",
  // Says what it costs rather than only that it happened, for the reason
  // `agent.removed` does: withdrawing closed the record in the same statement,
  // so this row is the last thing that can explain where the work went.
  "transfer.withdrawn":
    "The sale does not stand, and the record is no longer open to you. Nothing moved.",
  // THE ONLY DETAIL IN THIS TABLE THAT IS NOT THE WHOLE SENTENCE. Every other
  // row here says everything it can, because the API carries no prose into a
  // feed. This verb does — ONDO's own account of its own failure, which is the
  // one thing no record holds — so the row prints that reason after this line
  // and this line only has to frame it. See `payloadFor`'s `sale.reversed`.
  "sale.reversed":
    "Nothing about the work moved and nobody was told it had sold. Record the sale again when you are ready.",
  // SAYS WHAT HAS NOT CHANGED, which is the whole of what a seller needs to
  // know and the thing they are most likely to get wrong. A dispute is alarming
  // and changes nothing: the sale stands, the work is not back, and the next
  // step is to read what the buyer wrote and answer it on the record.
  "claim.disputed":
    "The sale still stands and the work has not come back to you. Open the work's record to read what they said.",
  // NAMES NOTHING THE ADDITION CONTAINS, which is the restraint the whole
  // feature keeps: the row says an approval is wanted and sends the reader to
  // the record, where what they are approving is readable because they are a
  // party to the sale.
  "sale.addition_proposed":
    "Nothing is added to the work's record until every party to the sale approves it. Open the record to read what is proposed.",
  "sale.addition_answered":
    "One party to the sale refused it, so nothing was added. The record says what it said before, and you may propose again.",
  "sale.addition_approved":
    "Every party to the sale approved it, so it is part of the work's record now.",
  // NAMES NO FIGURE, the rule this whole file keeps and the API enforces:
  // `payloadFor` carries no money into a feed, because a settlement's figures
  // are disclosed by one narrow definer to two named parties. The row sends the
  // reader to the screen that may show them.
  "settlement.due":
    "A work sold under one of your covenants has been resold inside its term. What it owes is on Resale settlements.",
  // IT SAYS WHAT IS *NOT* HAPPENING, and that sentence is OND-77's second half.
  // The ticket's finding was about this row: "'A payout is available' is
  // misleading — the nearest thing means a gallery has recorded paying you and
  // invites you to confirm or dispute; nothing is available to collect."
  //
  // The phrase itself appears nowhere in this codebase, and the title here has
  // said "recorded paying you" since the row was built — so what the ticket
  // names as misleading is what the row left UNSAID. ONDO holds no money, moves
  // none and releases none; this is somebody else's claim about a payment they
  // say they already made, and a reader who took it for a balance waiting for
  // them would confirm it to collect it. So the first clause says so, and only
  // then does it ask for the answer.
  "settlement.remitted":
    "Nothing is waiting here to be collected: this is the gallery's record of a payment it says it already made. Confirm it if the money arrived, or dispute it if it did not. Your answer is recorded beside theirs and never overwrites it.",
  "settlement.answered":
    "The artist has confirmed or disputed a remittance you recorded. What they said is on the settlement.",
};

export const ACTIONS: Record<NotificationType, string> = {
  "verification.requested": "Review",
  "verification.referred": "Review",
  "verification.answered": "View record",
  "verification.attested": "View record",
  // "View case" on all three, which is a destination none of the rows above
  // has: the service case is where the target, the reason and the fee sit
  // together, and sending a reader to the work's record would show them the
  // claim and none of the service.
  "verification.evidence_requested": "View case",
  "verification.extended": "View case",
  "verification.assigned": "View case",
  "attribution.claimed": "View record",
  "affiliation.claimed": "Review",
  "affiliation.answered": "View claim",
  "work.registered": "View record",
  "collection.shared": "View collection",
  "agent.assigned": "View record",
  // No action. The work is unreadable now, and an "Open" that 404s would be
  // worse than none — see `NOT_ROUTED` for the rows this build refuses to link.
  "agent.removed": "",
  // The work is still readable while the term runs, so there IS somewhere to
  // send them — and it is the place the renewal control lives.
  "mandate.expiring": "View record",
  // And afterwards it is not, which is `agent.removed`'s case exactly: an "Open"
  // that 404s would be worse than none.
  "mandate.expired": "",
  // ROUTED, all three, and the test is the one `consignment.revision_proposed`
  // passes rather than an assumption: nobody has lost a read. The appointment is
  // live — `ondo.live_mandate_ids()` is in the trigger's own WHERE clause — and
  // 20261003099000 is explicit that an agent changing firms "neither retargets
  // this grant nor loses it", so the reader can still open the work they are
  // being asked to look at. That is why these need no entry in
  // `SURVIVES_AN_UNREADABLE_SUBJECT` the way `agent.removed` does.
  "agent.organisation_joined": "Review appointment",
  "agent.organisation_left": "Review appointment",
  "agent.authority_changed": "Review appointment",
  "representation.requested": "Review",
  "representation.answered": "View request",
  "consignment.offered": "View record",
  "consignment.answered": "View record",
  // BOTH ARE ROUTED, unlike the three endings below, and the difference is that
  // nobody has lost a read: the consignment is still live either way, so the
  // work's record is open to both parties and the terms are on it.
  "consignment.revision_proposed": "View terms",
  "consignment.revision_answered": "View terms",
  // ALL THREE ARE ROUTED, and the one to check is `offer.refused` to the BUYER:
  // nothing about an offer opens or closes a read, so whatever let them see the
  // work when they bid still does. An offer is refused without touching a grant
  // — `20261003097000` decision 15 — which is what separates these from the
  // three endings below.
  "offer.received": "View offer",
  "offer.refused": "View offer",
  "offer.answered": "View offer",
  // ROUTED, all three, and the reason is the read decision 5 added: a sale
  // party reaches the entry whether or not they reach the work, so "Open" never
  // 403s here the way it would for a gallery whose consignment has ended.
  "sale.addition_proposed": "View record",
  "sale.addition_answered": "View record",
  "sale.addition_approved": "View record",
  // No action. Ending the consignment ended the access in the same statement, so
  // an "Open" would 403 for the gallery that just lost it — the same refusal
  // `agent.removed` makes above.
  "consignment.ended": "",
  "loan.proposed": "View record",
  "loan.answered": "View record",
  // No action. Ending a loan revokes the borrower's work grant in the same
  // statement, so an "Open" would 403 for the museum that just lost it — the
  // same refusal `consignment.ended` and `agent.removed` make above.
  "loan.ended": "",
  "transfer.offered": "Answer",
  "transfer.answered": "View record",
  // No action. Withdrawing closed `ondo.transfer_offered_work_ids()` in the
  // same statement, so an "Open" would 403 for the buyer who just lost it — the
  // refusal `agent.removed` and `consignment.ended` both make above.
  "transfer.withdrawn": "",
  // No action, and for a different reason from the three above: those have
  // nowhere to send the reader, while this one has nowhere USEFUL. The work is
  // unreadable — a reversed sale stops standing, so the firm loses
  // `placed_work_ids()` — and what the reader needs next is to record the sale
  // again on the Sell wizard, which is not a link off a notification row.
  "sale.reversed": "",
  // The work IS readable here, unlike the reversal directly above: both
  // recipients are recorded parties to a sale that still stands, so
  // `20261003098000` decision 5 keeps its line and its attestations in reach.
  "claim.disputed": "View work",
  "settlement.due": "View settlement",
  "settlement.remitted": "Answer",
  "settlement.answered": "View settlement",
};

/** What each row calls the thing it is about, before the label. */
export const SUBJECT_PREFIXES: Record<NotificationType, string | null> = {
  "verification.requested": "Work",
  "verification.referred": "Work",
  "verification.answered": "Work",
  "verification.attested": "Work",
  // "Work", because that is what the row names — the case is about one picture,
  // and the studio is printed as the counterparty.
  "verification.evidence_requested": "Work",
  "verification.extended": "Work",
  "verification.assigned": "Work",
  "attribution.claimed": "Work",
  "affiliation.claimed": "With",
  "affiliation.answered": "With",
  "work.registered": "Work",
  "collection.shared": "Collection",
  "agent.assigned": "Work",
  "agent.removed": "Work",
  "mandate.expiring": "Work",
  "mandate.expired": "Work",
  // "Work" and not "Agent", although the news is about the agent. The prefix
  // labels the SUBJECT LABEL beside it, which is the work's title — the agent's
  // name is printed by the `from` line off the actor snapshot, so "Agent: Pool
  // with Two Figures" would mislabel the one thing it sits next to.
  "agent.organisation_joined": "Work",
  "agent.organisation_left": "Work",
  "agent.authority_changed": "Work",
  "consignment.offered": "Work",
  "consignment.answered": "Work",
  "consignment.ended": "Work",
  "consignment.revision_proposed": "Work",
  "consignment.revision_answered": "Work",
  "offer.received": "Work",
  "offer.refused": "Work",
  "offer.answered": "Work",
  "sale.addition_proposed": "Work",
  "sale.addition_answered": "Work",
  "sale.addition_approved": "Work",
  "loan.proposed": "Work",
  "loan.answered": "Work",
  "loan.ended": "Work",
  "representation.requested": "Work",
  "representation.answered": "Work",
  "transfer.offered": "Work",
  "sale.reversed": "Work",
  "claim.disputed": "Work",
  "transfer.answered": "Work",
  "transfer.withdrawn": "Work",
  "settlement.due": "Work",
  "settlement.remitted": "Work",
  "settlement.answered": "Work",
};

export const NOTIFICATIONS_COPY = {
  loading: "Loading your notifications…",
  /**
   * The banner, and a deliberate rewrite of the one in the mock.
   *
   * That one called this an art-market hub, "where the market reaches you", and
   * promised fees earned and sales to confirm. ONDO records who held a work and
   * what stands behind that; it has no transfer, no sale and no fee, so a banner
   * describing a marketplace would be the interface promising what the registry
   * cannot do. What is left is true and is enough.
   */
  lead: "What the registry needs from you.",
  banner:
    "Other ONDO members ask you to confirm things you are positioned to know — a link in a chain of title, how a work changed hands, a document, a connection somebody claims with you. Answering builds the record.",
  /** The same sentence the Verifications screen gives, because a reader here needs it here. */
  noObligation: "Nobody is obliged to answer, and nothing is concluded from silence.",
  from: "From",
  unread: "New",
  markAllRead: "Mark all as read",
} as const;

/** The counters across the top. `id` is what the model fills in. */
export const TILES = [
  { id: "answer", label: "TO ANSWER" },
  { id: "passed", label: "PASSED TO YOU" },
  { id: "affiliations", label: "AFFILIATION CLAIMS" },
  { id: "unread", label: "UNREAD" },
] as const;

export type TileId = (typeof TILES)[number]["id"];

export const EMPTY_COPY = {
  never: {
    lead: "Nothing has been sent to you.",
    detail:
      "When somebody asks you to verify something, answers a question you asked, or shares a collection with you, it will appear here.",
  },
  filtered: {
    lead: "Nothing in this group.",
    detail: "Everything else is under All.",
  },
} as const;

/**
 * What this screen does not show, said out loud.
 *
 * The same voice `NOT_YET_COPY` uses on a planned destination, and here for the
 * same reason: the notifications design this was built from is full of transfer
 * requests, fee demands and payment confirmations, and a reader who has seen it
 * will notice they are gone. Naming the missing piece explains the product; a
 * silent omission reads as a bug, and a faked row would be worse than either.
 */
export const NOT_HERE = {
  lead: "Transfers, fees and payments are not here.",
  detail:
    "A sale needs a transfer — an offer one side makes and the other accepts — and the registry cannot express one yet, so there are no fees to settle and no payments to confirm. Until that exists, nothing in this inbox will ask you for money.",
} as const;

/**
 * A notification's tone, in the shared tag's vocabulary.
 *
 * Kept as a table rather than renaming `Tone`: the notification words say what
 * the row IS to this feature — an ask, an answer, a piece of news, a thing
 * closed off — and the tag words say how it should read on any screen. The two
 * are allowed to stop lining up.
 */
export const TONE_TAGS: Record<Tone, TagTone> = {
  ask: "pending",
  answer: "answer",
  news: "quiet",
  done: "verified",
};
